import { Component, OnInit, ViewChild } from "@angular/core";
import { BasketService } from "src/app/services/basket.service";
import { NgbDropdownConfig, NgbDropdown, NgbPopover } from "@ng-bootstrap/ng-bootstrap";
import { ApplicationService } from "src/app/services/application.service";
import { Router } from "@angular/router";
import { GroupedBasketArticles } from "src/app/interfaces/grouped-basket-articles";
import { Basket } from "src/app/interfaces/basket";
import { ApiResponse } from "src/app/interfaces/api-response";
import { DeviceDetectorService } from "ngx-device-detector";
import { timer } from "rxjs";
import { HelperService } from "src/app/services/helper.service";
import { AccountService } from "src/app/services/account.service";
import { isThisTypeNode } from "typescript";
import { FreeShippingProgressComponent } from "../free-shipping-progress/free-shipping-progress.component";

@Component({
    selector: "app-basket-preview",
    templateUrl: "./basket-preview.component.html",
    styleUrls: ["./basket-preview.component.scss"],
    standalone: false
})
export class BasketPreviewComponent implements OnInit {
  @ViewChild(FreeShippingProgressComponent) freeShippingProgress: FreeShippingProgressComponent;
  @ViewChild('popover') popover: NgbPopover;
  
  numArticles = 0;
  articles: any;
  basket: Basket;
  selectedCompanyGroup: string;
  changing: boolean;
  loading = false;
  groupedArticles: GroupedBasketArticles;
  timer: any;
  basketSum: any = {
    parts: 0,
    bicycles: 0,
    total: 0
  };
  showroomMode = false;
  adding: boolean;
  imgSrc: any;
  addedArticles: any;
  dropdown: any;
  isMobile: boolean;
  cursorOutModal: boolean;
  isDealer: boolean;
  showFreeShippingPopover: boolean = false;
  popoverTimer: any;
  thresholdAvailable = false;

  constructor(
    private basketService: BasketService,
    config: NgbDropdownConfig,
    private applicationService: ApplicationService,
    private router: Router,
    private deviceDetector: DeviceDetectorService,
    private helperService: HelperService,
    private accountService: AccountService,
  ) {
    this.isMobile = this.deviceDetector.isMobile();
    this.isDealer = !this.applicationService.isPartnerPortal();
    this.basketService.changeEmitted$.subscribe(response => {
      switch (response["type"]) {
        case "deleteArticle":
          this.deleteArticle(response);
          this.getActiveBasket(true);
          break;

        case "deleteBasket":
        case "addArticle":
          this.addedArticles = [];
          this.getActiveBasket(false); // Fetch full basket data for popover
          if (response["type"] === "addArticle") {
            if (response["articles"] && response["articles"].length) {
              const articles = response["articles"];
              this.addedArticles = articles;
              this.adding = true;
              
              // Show free shipping popover when article is added
              setTimeout(() => {
                if (this.popover && this.basket && !this.showroomMode) {
                  // Only show popover if there's a shipping threshold
                  if (this.freeShippingProgress && this.freeShippingProgress.thresholdAmount > 0) {
                    this.popover.open();
                  }
                }
              }, 500); // Increased delay to ensure basket is loaded
              
              // Clear any existing timer
              if (this.popoverTimer) {
                clearTimeout(this.popoverTimer);
              }
              
              // Auto-hide popover after 5 seconds
              this.popoverTimer = setTimeout(() => {
                if (this.popover) {
                  this.popover.close();
                }
              }, 5000);
              
              timer(articles.length * 3000 + (articles.length * 250)).subscribe(() => {
                this.adding = false;
              })
            }
          }
          break;
        case "updateArticle":
          this.getActiveBasket(response["reloadBasket"] ? false : true);
          break;

        case "unlocked":
          if (this.basket && this.basket.locked) {
            this.basket.locked = false
          }
          break;

        default:
          if (response["type"] !== "totalChange" && response["type"] !== "updateCheckoutStatusArticle") {
            this.getActiveBasket(true);
          }
          break;
      }
    });
    config.autoClose = true;
    this.getShowroomMode();
  }

  getShowroomMode() {
    this.showroomMode = this.applicationService.getShowroomMode(
      this.showroomMode
    );
  }

  deleteArticle(basketAction) {
    if (this.basket) {
      this.basketService.removeFromBasket(
        this.basket,
        basketAction["basketArticleId"]
      );
      this.groupedArticles = this.basketService.groupArticles(
        this.basket.items
      );
      this.calcTotal(this.basket.items);
    }
  }

  showUpdateAnimation() {
    const self = this;
    this.changing = true;
    setTimeout(() => {
      self.changing = false;
    }, 400);
  }

  ngOnInit() {
    this.applicationService.changeEmitted$.subscribe(response => {
      this.getShowroomMode();
      if (this.basket && this.basket) {
        this.calcTotal(this.basket.items);
      }
    });

    this.applicationService.emitChangeCompanyGroupChanged$.subscribe(
      response => {
        this.basket = null;
        this.selectedCompanyGroup = this.applicationService.getSelectCompanyGroupCode();
      }
    );

    this.selectedCompanyGroup = this.applicationService.getSelectCompanyGroupCode();
    this.getActiveBasket(true);
  }

  calcTotal(articles) {
    if (this.basket) {
      this.basketSum = this.basketService.calcTotal(
        articles,
        "basketPreview",
        this.showroomMode,
        this.basket.shipping_cost
      );
      
      // No need to reload - data is passed via inputs and will update automatically via ngOnChanges
    }
  }

  getActiveBasket(countOnly) {
    const self = this;
    this.loading = true;
    const service = countOnly ? this.basketService.getActiveBasketIdAndCount() : this.basketService.getActiveBasket()
    service.then((responseData: ApiResponse) => {
      this.loading = false;
      if (this.helperService.checkResponse(responseData)) {
        // Show change in basket article count
        if (
          countOnly &&
          responseData.result["item_count"] !== self.numArticles
        ) {
          self.showUpdateAnimation();
        }

        self.numArticles = countOnly ? responseData.result["item_count"] : responseData.result["items"].length;

        if (!countOnly) {
          self.basket = responseData.result;

          self.articles = responseData.result["items"];
          self.groupedArticles = this.basketService.groupArticles(
            self.articles
          );
          this.calcTotal(self.articles);
        }
      } else {
        self.numArticles = 0;
        self.articles = [];
        self.groupedArticles = new GroupedBasketArticles();
      }
    });
  }

  showDropdown(dropdown) {
    if (!window.location.href.endsWith("basket")) {
      this.dropdown = dropdown;
      const self = this;
      self.getActiveBasket(false);

      this.timer = setTimeout(function() {
        if (!window.location.href.endsWith("basket")) {
          dropdown.open();
        }
      }, 500);
    }
  }

  hideDropdown(dropdown = null) {
    if (dropdown) {
      dropdown.close();
    }
    if (this.dropdown) {
      this.dropdown.close();
    }
    clearTimeout(this.timer);
  }

  // Only close the dropdown when there is no article modal active
  closeDropdown(dropdown: NgbDropdown, direct = false) {
    const articleModal = document.getElementById("modalContainer").getElementsByTagName("app-article-modal");
    if (!articleModal.length) {

      // If cursor outside window - set timeout - to prevent accidental closing
      this.cursorOutModal = true;

      if (direct) {
        dropdown.close();
      } else {
        timer(300).subscribe(() => {
          if (this.cursorOutModal) {
            dropdown.close();
          }
        })
      }
    }
  }

  gotoBasket(dropdown: NgbDropdown) {
    if (this,this.deviceDetector.isDesktop()) {
      this.basketService.cancelCall();
    }
    this.loading = false;
    if (typeof dropdown !== "undefined" && this.deviceDetector.isDesktop()) {
      dropdown.close();
      this.router.navigateByUrl(this.selectedCompanyGroup + "/basket");
    } else {
      dropdown.open();
    }
  }

  unlockBasket() {
    this.basketService.unlockBasket(this.basket.id)
  }

  stopTimer(dropdown: NgbDropdown) {
    if (!this.dropdown?.isOpen()) {
      clearTimeout(this.timer);
    }
  }

  closeFreeShippingPopover() {
    if (this.popover) {
      this.popover.close();
    }
    if (this.popoverTimer) {
      clearTimeout(this.popoverTimer);
    }
  }

  // Helper getters to extract shipping threshold data from basket
  getShippingThreshold(type: 'Parts' | 'Bicycles'): number {
    const shippingCostArray = this.basket?.selected_shipping_costs?.[type];
    if (shippingCostArray && shippingCostArray.length > 0) {
      return shippingCostArray[0]?.shipping_threshold?.threshold?.value || 0;
    }
    return 0;
  }

  getShippingCostValue(type: 'Parts' | 'Bicycles'): number {
    const shippingCostArray = this.basket?.selected_shipping_costs?.[type];
    if (shippingCostArray && shippingCostArray.length > 0) {
      return shippingCostArray[0]?.shipping_costs?.value || 0;
    }
    return 0;
  }

  getShippingCurrency(type: 'Parts' | 'Bicycles'): string {
    const shippingCostArray = this.basket?.selected_shipping_costs?.[type];
    if (shippingCostArray && shippingCostArray.length > 0) {
      return shippingCostArray[0]?.shipping_costs?.currency || 'EUR';
    }
    return 'EUR';
  }
}
